import { useNavigate } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import { BookOpen, Globe, Rocket, ChevronDown } from "lucide-react";
import { useState } from "react";

const paths = [
  {
    icon: BookOpen,
    role: "Student",
    tagline: "Join free online classes taught by passionate volunteers",
    heading: "Learn a Language",
    items: ["French", "Spanish", "Chinese", "ESL Programs"],
    color: "bg-[#D6E6FA]",
    borderAccent: "border-[#3C8DE4]",
    cta: "Register as Student",
    internal: true,
    to: "/register",
  },
  {
    icon: Globe,
    role: "Tutor",
    tagline: "Help learners build language skills while gaining teaching and leadership experience",
    heading: "Teach a Language",
    items: ["Flexible hours", "Gain volunteer hours", "Make a global impact"],
    color: "bg-[#FBE7C6]",
    borderAccent: "border-[#E6A817]",
    cta: "Apply as Tutor",
    internal: false,
    to: "https://forms.gle/MTPSXyymcXQoZcfb6",
  },
  {
    icon: Rocket,
    role: "Team",
    tagline: "Help grow our global learning community",
    heading: "Join Our Team",
    items: ["Marketing", "Finance", "Administration", "Outreach"],
    color: "bg-[#FFAEBC]",
    borderAccent: "border-[#E05A73]",
    cta: "Apply as Team Member",
    internal: false,
    to: "https://docs.google.com/forms/d/e/1FAIpQLSfPAYDoFjimw7ESCDdVcp8dXMN2PyRUSN_lVfJgcLzy-CpvOQ/viewform",
  },
];

const faqData = [
  { q: "Is Virtually Verbal free?", a: "Yes! All of our language learning programs are offered free of charge thanks to our dedicated volunteer tutors and team members." },
  { q: "Who can sign up as a student?", a: "Anyone interested in learning a language is welcome to apply. We offer programs for learners of different ages and experience levels (Basic & Intermediate)." },
  { q: "What languages do you offer?", a: "We currently offer programs in French, Spanish, Chinese, and ESL (English as a Second Language). Available languages may vary based on tutor availability." },
  { q: "Do I need teaching experience to become a tutor?", a: "No. While teaching experience is helpful, it is not required. We welcome passionate individuals who are eager to share their language skills and support learners." },
  { q: "How much time is required from tutors?", a: "Most tutors teach one or more sessions per week. Scheduling is flexible, and we work with tutors to find a commitment that fits their availability." },
  { q: "Are tutor and team positions paid?", a: "No. Virtually Verbal is a student-led nonprofit organization, and all tutor and team positions are volunteer-based." },
  { q: "Can I receive volunteer hours?", a: "Yes! We can provide volunteer hour verification upon request for school, work, or community service requirements." },
  { q: "What does the team do?", a: "Our team supports Virtually Verbal through areas such as marketing, outreach, finance, administration, and program development." },
  { q: "How are students matched with tutors?", a: "We review each student's application and match them with a tutor based on language preference, availability, and program needs." },
  { q: "How do classes take place?", a: "Classes are conducted online through virtual platforms (Zoom), allowing learners and tutors from around the world to connect." },
  { q: "How long are programs?", a: "Most language programs run for 8 weeks, with one, one-hour session per week. Our ESL programs run for 6 weeks, with two, one-hour sessions per week." },
  { q: "How can I contact you?", a: "You can reach us at officialvirtuallyverbal@gmail.com or through our social media channels." },
];

const FaqItem = ({ item, isOpen, onToggle }) => (
  <div className="brutalist-card overflow-hidden" data-testid={`faq-item`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left font-semibold text-sm hover:bg-[#FDFBF7] transition-colors"
    >
      <span>{item.q}</span>
      <ChevronDown className={`w-5 h-5 shrink-0 ml-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="px-5 pb-5 pt-0 text-sm text-[#4A4A4A] leading-relaxed border-t border-[#0A0A0A]/10">
        {item.a}
      </div>
    )}
  </div>
);

const JoinUsPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="py-20 sm:py-28 px-6 bg-gradient-to-b from-[#FFAEBC]/25 to-[#FDFBF7]">
        <div className="container mx-auto text-center max-w-3xl">
          <div className="inline-block bg-[#3C8DE4] text-white border-2 border-[#0A0A0A] rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider mb-6" data-testid="join-badge">
            Join Us
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
            Join the Virtually Verbal Community!
          </h1>
          <p className="text-base sm:text-lg text-[#4A4A4A] leading-relaxed">
            Join learners, tutors, and volunteers from around the world in building a more connected global community.
          </p>
        </div>
      </section>

      {/* 3 Paths */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-3 gap-6">
            {paths.map((p) => (
              <div key={p.role} className={`brutalist-card p-8 flex flex-col border-t-4 ${p.borderAccent}`} data-testid={`path-${p.role.toLowerCase()}`}>
                <div className={`w-14 h-14 rounded-full ${p.color} border-2 border-[#0A0A0A] flex items-center justify-center mb-5`}>
                  <p.icon className="w-7 h-7" strokeWidth={2} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#4A4A4A] mb-1">{p.role}</span>
                <h3 className="text-xl font-bold mb-2">{p.heading}</h3>
                <p className="text-sm text-[#4A4A4A] mb-5">{p.tagline}</p>
                <ul className="space-y-2 mb-8 flex-1">
                  {p.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#3C8DE4] shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                {p.internal ? (
                  <button
                    onClick={() => navigate(p.to)}
                    className="brutalist-btn-primary text-xs w-full text-center"
                    data-testid={`join-cta-${p.role.toLowerCase()}`}
                  >
                    {p.cta}
                  </button>
                ) : (
                  <a
                    href={p.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brutalist-btn-primary text-xs w-full text-center block"
                    data-testid={`join-cta-${p.role.toLowerCase()}`}
                  >
                    {p.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#4A4A4A] mt-8">
            Virtually Verbal is a student-led nonprofit organization. Tutor and team positions are volunteer-based, and volunteer hours can be provided upon request.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-white border-y-2 border-[#0A0A0A]" data-testid="faq-section">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {faqData.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default JoinUsPage;
